import { Component, OnInit, OnDestroy, ChangeDetectionStrategy } from '@angular/core';
import { DatasetActionContainerService, DatasetEvents } from 'app/main/components/dataset/services/dataset-action-container.service';
import { DatasetNavigatorService } from 'app/main/components/dataset/services/dataset-navigator.service';
import { DatasetService } from 'app/main/components/dataset/services/dataset.service';
import { BaseStepViewComponent } from 'app/main/components/dataset/components/common-step/base-step-view.class';
import { takeUntil, finalize, filter } from 'rxjs/operators';
import { DatasetRecordService } from 'app/main/components/dataset/services/dataset-record.service';
import { AppService } from 'app/services/app.service';
import { IFieldDefinition } from 'app/interfaces';
import { MatDialog } from '@angular/material/dialog';
import { HttpHeaders } from '@angular/common/http';
import FilterFields from './form.fields';
import { Validators } from '@angular/forms';
import { FormGroup, FormBuilder, FormControl, FormArray } from '@angular/forms';
import { StepService } from 'app/main/components/dataset/services/step-state.service';
import moment from 'moment';
import { AssistantService } from 'app/main/components/assistant/assistant.service';
import { AssistantContextService } from 'app/main/components/assistant/assistant-context.service';

export interface IpriceRule {
	price: any;
	option_id: any;
	valid_from: any;
	valid_to: any;
}

export interface IAge {
	field: string;
	from: number;
	to: number;
}

export interface IPriceServiceData {
	title: string;
	code: string;
	formArrayName: string;
	canBeRemoved: boolean;
	age: IAge;
	defaultPriceRule: IpriceRule;
	price_rules?: IpriceRule[];
	/** criterio con regola partecipante personalizzata (es. creato dall'assistente): sola lettura nello step */
	custom?: boolean;
	rule_description?: string;
	condition_rule?: string;
	count_rule?: string;
}

export interface PriceServices {
	FIXED1: IPriceServiceData;
	ADT: IPriceServiceData;
	CHD: IPriceServiceData;
	INF: IPriceServiceData;
	SNR: IPriceServiceData;
}

@Component({
    selector: 'price-step',
    templateUrl: './step.component.html',
    styleUrls: ['./step.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})

export class PriceStepComponent extends BaseStepViewComponent implements OnInit, OnDestroy{
	now = moment(new Date()).format('YYYY-MM-DD 00:00:00');

	public selectedTripServiceRule: any;
	public loadedTripServiceRule: any;

	public filterFormGroup: FormGroup;

	private priceServicesRemoved: IPriceServiceData[] = [];
	private priceRulesRemoved: IpriceRule[] = [];
	public priceServices: IPriceServiceData[] = [];
	/** criteri fuori dalle categorie dello step: mostrati in sola lettura, il salvataggio li conserva (server) */
	public customServices: IPriceServiceData[] = [];
	public priceServicesCollection: any = {};
	public fileterFields = FilterFields;

	// definire servizio??
	mockServices: PriceServices = {
		FIXED1: {
			title: 'Prezzi gruppo',
			canBeRemoved: false,
			code: 'FIXED1',
			formArrayName: 'FIXED1',
			age: {field: 'age_adt', from: 0, to: 0},
			defaultPriceRule: {
				price: null, 
				option_id: null, 
				valid_from: this.now, 
				valid_to: null
			},
		},
		ADT: {
			title: 'Prezzo Adulto',
			canBeRemoved: false,
			code: 'ADT',
			formArrayName: 'ADT',
			age: {field: 'age_adt', from: 18, to: 64},
			defaultPriceRule: {
				price: null, 
				option_id: null, 
				valid_from: this.now, 
				valid_to: null
			},
		},
		CHD: {
			title: 'Prezzo Bambino',
			canBeRemoved: true,
			code: 'CHD',
			formArrayName: 'CHD',
			age: {field: 'age_chd', from: 2, to: 17},
			defaultPriceRule: {
				price: null, 
				option_id: null, 
				valid_from: this.now, 
				valid_to: null
			},
		},
		INF: {
			title: 'Prezzo Neonato',
			canBeRemoved: true,
			code: 'INF',
			formArrayName: 'INF',
			age: {field: 'age_inf', from: 0, to: 1},
			defaultPriceRule: {
				price: null, 
				option_id: null, 
				valid_from: this.now, 
				valid_to: null
			},
		},
		SNR: {
			title: 'Prezzo Senior',
			canBeRemoved: true,
			code: 'SNR',
			formArrayName: 'SNR',
			age: {field: 'age_snr', from: 65, to: 99},
			defaultPriceRule: {
				price: null, 
				option_id: null, 
				valid_from: this.now, 
				valid_to: null
			},
		}
	};

	buttons: any = PriceStepComponent.defaultButtons();
	/** prezzi gestiti dall'assistente: step in sola lettura (il server rifiuta comunque il salvataggio) */
	assistantPricingLocked = false;

	static defaultButtons(): any{
		return {
			CHD: {title: 'CHILDREN'},
			INF: {title: 'INFANT'},
			SNR: {title: 'SENIOR'}
		};
	}

	constructor(
		public appService: AppService,
		public datasetRS: DatasetRecordService,
		public datasetACS: DatasetActionContainerService,
		public datasetService: DatasetService,
		protected datasetNavigatorService: DatasetNavigatorService,
		protected matDialog: MatDialog,
		protected formBuilder: FormBuilder,
		protected stepService: StepService,
		protected assistantService: AssistantService,
		protected assistantContextService: AssistantContextService,
	) {
		super(datasetACS, datasetNavigatorService);
	}

	getBaseRoute(): string{
		return '/dataset/gyg_based_trip_services';
	}

	getHttpHeaders(): HttpHeaders {
		return this.datasetACS.getContextualDomainHeaders();
	}

	canShowAge(): boolean{
		return Object.keys(this.priceServicesCollection).length > 1;
	}

	processResponse(response: PriceServices): void {
		const keys = Object.keys(response);

		keys.forEach((key) => {
			const obj: IPriceServiceData = response[key];
			// regola personalizzata: niente form (le categorie dello step sono fisse), solo riepilogo
			if (obj.custom) {
				this.customServices.push(obj);
				return;
			}
			this.buildForm(obj);
		});
		// buildForm aggiunge controlli abilitati: riapplica la sola lettura
		this.applyAssistantLock();
	}

	initFormGroup(): void {
		this.filterFormGroup = this.formBuilder.group({
			age_adt: new FormControl(),
			age_chd: new FormControl(),
			age_inf: new FormControl(),
			age_snr: new FormControl(),
			ADT: this.formBuilder.array([]),
			CHD: this.formBuilder.array([]),
			INF: this.formBuilder.array([]),
			SNR: this.formBuilder.array([]),
			FIXED1: this.formBuilder.array([])
		});
	}

	setUpPriceTiers(group: string, priceRule?: IpriceRule): void {
		if (!priceRule) priceRule = this.mockServices[group].defaultPriceRule;
		this.priceRules(group).push(this.formBuilder.group({
			price: [priceRule.price, Validators.required],
			option_id: priceRule.option_id,
			valid_from: [priceRule.valid_from, Validators.required],
			valid_to: priceRule.valid_to
		}));
	}
	
	priceRules(group: string): FormArray {
		return <FormArray>this.filterFormGroup.get(group);
	}

	ngOnInit(): void {
		this.initFormGroup();
		this.loadPricing(this.defaultTripServiceRule());

		// l'assistente vede le regole a schermo e se ci sono modifiche non salvate
		this.assistantContextService.setSnapshot('prices_step', () => this.assistantSnapshot());
		// step prezzi del pacchetto: punto della UI dove l'assistente è abilitato
		this.assistantContextService.anchor('prices_step', 'trip_packages_pricing');
		this.assistantService.pricingManaged$
		.pipe(takeUntil(this._unsubscribeAll))
		.subscribe(locked => {
			this.assistantPricingLocked = locked;
			this.applyAssistantLock();
		});

		// conferma dall'assistente: il salvataggio di questo step cancella e ricrea tutti i prezzi,
		// quindi il form va riallineato al server prima di qualsiasi altro salvataggio
		this.assistantService.entityChanged
		.pipe(
			takeUntil(this._unsubscribeAll),
			filter(entity => entity.type === 'trip_packages' && entity.id == this.datasetRS.recordId)
		)
		.subscribe(() => {
			const hadUnsavedChanges = this.filterFormGroup.dirty;
			this.reloadPricing();
			this.appService.showSuccessMessage(hadUnsavedChanges
				? 'Prezzi aggiornati dall\'assistente: le modifiche non salvate nel form sono state scartate.'
				: 'Prezzi aggiornati dall\'assistente.');
		});
	}

	ngOnDestroy(): void {
		this.assistantContextService.clearSnapshot('prices_step');
		this.assistantContextService.release('prices_step');
		super.ngOnDestroy();
	}

	defaultTripServiceRule(): string {
		return (this.datasetRS.record.value.trip_service_rule_type == '0') ? 'ADT' : 'FIXED1';
	}

	reloadPricing(): void {
		this.priceServicesCollection = {};
		this.priceServices = [];
		this.customServices = [];
		this.priceServicesRemoved = [];
		this.priceRulesRemoved = [];
		this.buttons = PriceStepComponent.defaultButtons();
		this.initFormGroup();
		this.applyAssistantLock();
		this.loadPricing(this.defaultTripServiceRule());
	}

	applyAssistantLock(): void {
		if (!this.filterFormGroup) return;
		if (this.assistantPricingLocked) this.filterFormGroup.disable({ emitEvent: false });
		else this.filterFormGroup.enable({ emitEvent: false });
	}

	assistantSnapshot(): any {
		if (!this.filterFormGroup) return null;
		const value = this.filterFormGroup.getRawValue();
		const visible = {};
		for (const code of Object.keys(this.priceServicesCollection)) {
			visible[code] = (value[code] || []).slice(0, 20).map(rule => ({ valid_from: rule.valid_from, valid_to: rule.valid_to, price: rule.price }));
		}
		const custom = this.customServices.map(service => ({ title: service.title, condition_rule: service.condition_rule, count_rule: service.count_rule }));
		return { step: 'prices', unsaved_changes: this.filterFormGroup.dirty, visible, ...(custom.length ? { custom_criteria: custom } : {}) };
	}

	loadPricing(tripServiceRuleId: any): void{
		const httpParams: any = {trip_package_id: this.datasetRS.recordId};

		this.datasetACS.loading.next(true);
		this.datasetService.get<PriceServices>(this.getBaseRoute(), httpParams, this.getHttpHeaders())
		.pipe(takeUntil(this._unsubscribeAll),
			finalize(() => { 
				if (Object.keys(this.priceServicesCollection).length === 0) this.buildForm(tripServiceRuleId); 
			})
		).subscribe({
			next: (response) => {
				this.datasetACS.loading.next(false);
				this.processResponse(response);
			},
			error: () => {
				this.datasetACS.loading.next(false);
			}
		}); 
	}

	/** prezzo dal server (number_format PHP: "1,500.00") nel formato italiano */
	formatPrice(price: string | number): string {
		const value = typeof price === 'number' ? price : parseFloat(String(price).replace(/,/g, ''));
		return isNaN(value) ? String(price) : new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(value);
	}

	getPriceService(priceService: IPriceServiceData|string): IPriceServiceData {
		let code: string;
		let priceServ: IPriceServiceData;
		if (typeof priceService === 'object') {
			code = priceService.code;
			priceServ = priceService;
		} else {
			code = priceService;
			priceServ = this.mockServices[code];
		}
		return priceServ;
	}

	buildForm(priceService: IPriceServiceData|string): void {
		const priceServ: IPriceServiceData = this.getPriceService(priceService);

		if (!this.priceServicesCollection[priceServ.code]) {
			priceServ.age = {
				field: this.mockServices[priceServ.code].age.field,
				from: priceServ.age.from,
				to: priceServ.age.to
			};

			this.setAgeRange(priceServ);

			this.filterFormGroup.addControl(priceServ.age.field, new FormControl(priceServ.age.to));
			const ageFormField = this.filterFormGroup.get(priceServ.age.field);

			this.fixAgeField(this.filterFormGroup.get(priceServ.age.field).value, priceServ, true);

			ageFormField.valueChanges.subscribe(value => {
				this.fixAgeField(value, priceServ);
			});

			if (!priceServ.price_rules) {
				this.setUpPriceTiers(priceServ.code, priceServ.defaultPriceRule);
			} else {
				for (const priceRule of priceServ.price_rules) this.setUpPriceTiers(priceServ.code, priceRule);
			}
			
			this.priceServicesCollection[priceServ.code] = priceServ;
			delete this.buttons[priceServ.code];

			this.sortOrder();
		}
	}

	sortOrder(): void{
		this.priceServicesCollection = {
			...(this.priceServicesCollection['INF'] ? {'INF': this.priceServicesCollection['INF']} : null),
			...(this.priceServicesCollection['CHD'] ? {'CHD': this.priceServicesCollection['CHD']} : null),
			...(this.priceServicesCollection['ADT'] ? {'ADT': this.priceServicesCollection['ADT']} : null),
			...(this.priceServicesCollection['SNR'] ? {'SNR': this.priceServicesCollection['SNR']} : null),
			...(this.priceServicesCollection['FIXED1'] ? {'FIXED1': this.priceServicesCollection['FIXED1']} : null)
		};
	}

	formatLabel(value: number): string {
		if (value >= 99)  return '99+';
		return value.toString();
	}

	fixAgeField(value: number, priceService: IPriceServiceData, withSnr: boolean = false): void{
		if (value === 99) return;
		if (priceService.code === 'CHD'){
			this.priceServicesCollection['ADT'].age.from = (value + 1);

			if (value >= this.filterFormGroup.get('age_adt').value){
				this.filterFormGroup.get('age_adt').setValue(value + 1);
			}
		}
		if (priceService.code === 'INF' && this.priceServicesCollection['CHD']) {
			this.priceServicesCollection['CHD'].age.from = (value + 1);

			if (value >= this.filterFormGroup.get('age_chd').value){
				this.filterFormGroup.get('age_chd').setValue(value + 1);
			}
		} else if (priceService.code === 'INF' && !this.priceServicesCollection['CHD']){
			this.priceServicesCollection['ADT'].age.from = (value + 1);

			if (value >= this.filterFormGroup.get('age_adt').value){
				this.filterFormGroup.get('age_adt').setValue(value + 1);
			}
		}
		if (priceService.code === 'ADT' && this.priceServicesCollection['SNR']){
			this.priceServicesCollection['SNR'].age.from = (value + 1);

			if (value >= this.filterFormGroup.get('age_snr').value){
				this.filterFormGroup.get('age_snr').setValue(value + 1);
			}
		}
	}

	setAgeRange(priceServ: IPriceServiceData, withoutSetValue: boolean = false): void {
		const ageField: IFieldDefinition = this.fileterFields.get(priceServ.age.field);

		ageField.options = [];
		for (let i = priceServ.age.from; i <= 99; i++) {
			ageField.options.push({label: i.toString(), value: i});
		}
		if (!withoutSetValue) this.filterFormGroup.get(priceServ.age.field).setValue(priceServ.age.to);
	}

	isDisabledButton(): boolean {
		let isDisabledButton = true;
		if (this.priceServicesRemoved.length > 0 && this.filterFormGroup.valid) {
			isDisabledButton = false;
		} else if (this.priceRulesRemoved.length > 0 && this.filterFormGroup.valid) {
			isDisabledButton = false;
		} else if (this.filterFormGroup.valid && this.filterFormGroup.dirty) {
			isDisabledButton = false;
		}
		return isDisabledButton;
	}

	removePriceService(priceService: IPriceServiceData): void {
		this.priceServices = this.priceServices.filter((x: IPriceServiceData) => x.code !== priceService.code);
		delete this.priceServicesCollection[priceService.code]; // rimozione oggetto
		this.buttons[priceService.code] = priceService.title;

		this.clearFormArray(this.priceRules(priceService.code));
		this.filterFormGroup.get(priceService.age.field).setValue(null);

		if (priceService.code === 'SNR') this.filterFormGroup.get('age_adt').setValue(priceService.age.to);

		if (priceService.code === 'CHD' && this.priceServicesCollection['INF']) {
			this.priceServicesCollection['INF'].age.from = priceService.age.from;
		} else if (priceService.code === 'CHD' && !this.priceServicesCollection['INF']){
			this.priceServicesCollection['ADT'].age.from = priceService.age.from;
		}

		if (priceService.code === 'INF' && this.priceServicesCollection['CHD']){
			this.priceServicesCollection['CHD'].age.from = priceService.age.from;
		} else if (priceService.code === 'INF' && !this.priceServicesCollection['CHD']){
			this.priceServicesCollection['ADT'].age.from = priceService.age.from;
		}

		this.priceServicesRemoved.push(priceService);
	}

	removePriceRule(priceService: IPriceServiceData, i: number): void {
		this.priceRules(priceService.code).removeAt(i);
		this.priceRulesRemoved.push(this.filterFormGroup.get(priceService.code).value[i - 1]);
	}

	clearFormArray = (formArray: FormArray) => {
		while (formArray.length) {
			formArray.removeAt(formArray.length - 1);
		}
	}

	beforeSave(formData: any): any{
		if (this.priceServicesCollection['INF']) formData['age_from_inf'] = this.priceServicesCollection['INF'].age.from;
		if (this.priceServicesCollection['CHD']) formData['age_from_chd'] = this.priceServicesCollection['CHD'].age.from;
		if (this.priceServicesCollection['ADT']) formData['age_from_adt'] = this.priceServicesCollection['ADT'].age.from;
		if (this.priceServicesCollection['SNR']) formData['age_from_snr'] = this.priceServicesCollection['SNR'].age.from;
		return formData;
	}
	
	onSave(): void{
		this.filterFormGroup.disable();
		let formData = this.filterFormGroup.getRawValue();

		this.datasetACS.loading.next(true);
		const route = this.getBaseRoute() + '/create';

		formData['trip_package_id'] = this.datasetRS.recordId;
		formData = this.beforeSave(formData);

		this.datasetService.post<PriceServices>(route, formData, {headers: this.getHttpHeaders()})
		.pipe(takeUntil(this._unsubscribeAll))
		.subscribe({
			next: (response) => {
				this.datasetACS.loading.next(false);
				this.datasetACS.datasetEvent.next({eventName: this.datasetRS.recordId ? DatasetEvents.EDITED : DatasetEvents.CREATED });
				if(this.filterFormGroup){
					this.filterFormGroup.enable();
					this.filterFormGroup.reset();
				}

				this.priceServicesCollection = {};
				this.initFormGroup();
				this.processResponse(response);
				
				setTimeout(() => {
					this.stepService.setStepState({
						dataset_code: 'trip_packages',
						form_code: 'prices',
						record_id: this.datasetRS.recordId,
						status: 1
					});

					this.goNextStep();
				}, 200);
			},
			error: () => {
				this.datasetACS.loading.next(false);
				if(this.filterFormGroup){
					this.filterFormGroup.enable();
				}
			}
		});
	}
}
